import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { importsApi } from '@/api/endpoints'
import type { ImportBatch } from '@/api/types'
import { Card } from '@/components/ui'
import { errorMessage } from '@/utils/errors'
import { formatDateTime } from '@/utils/format'

/**
 * Import winstrom XML z FlexiBee (evidence banka). Stejný export jde nahrát
 * opakovaně — nové pohyby přibydou, známé se aktualizují a naše zpracování
 * (stav, poznámka, doklady) u nich zůstane.
 */
export default function ImportPage() {
  const [history, setHistory] = useState<ImportBatch[]>([])
  const [result, setResult] = useState<ImportBatch | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [dragging, setDragging] = useState(false)
  const input = useRef<HTMLInputElement>(null)

  const load = () => importsApi.history().then(setHistory)
  useEffect(() => {
    load()
  }, [])

  const upload = async (file: File | undefined) => {
    if (!file) return
    setError(null)
    setResult(null)
    setBusy(true)
    try {
      setResult(await importsApi.upload(file))
      await load()
    } catch (e) {
      setError(errorMessage(e, 'Import se nezdařil.'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="space-y-4">
      <h1 className="text-lg font-semibold">Import z FlexiBee</h1>

      <Card
        onDragOver={(e) => {
          e.preventDefault()
          setDragging(true)
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault()
          setDragging(false)
          upload(e.dataTransfer.files[0])
        }}
        className={dragging ? 'border-brand-500 ring-2 ring-brand-200' : ''}
      >
        <button
          type="button"
          disabled={busy}
          onClick={() => input.current?.click()}
          className="flex w-full flex-col items-center justify-center rounded-lg border-2 border-dashed border-slate-300 px-4 py-10 text-sm text-slate-500 transition hover:border-brand-500 hover:text-brand-600 disabled:opacity-50"
        >
          {busy ? (
            <span>Importuji…</span>
          ) : (
            <>
              <span className="font-medium">Přetáhni sem XML export nebo klikni</span>
              <span className="mt-1 text-xs text-slate-400">
                FlexiBee → Banka → vyfiltrovat pohyby bez dokladu → Export → XML (winstrom)
              </span>
            </>
          )}
        </button>
        <input
          ref={input}
          type="file"
          accept=".xml,application/xml,text/xml"
          className="hidden"
          onChange={(e) => {
            upload(e.target.files?.[0])
            e.target.value = ''
          }}
        />

        {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
        {result && (
          <div className="mt-4 rounded-lg bg-green-50 p-3 text-sm text-green-800">
            <p className="font-medium">
              Naimportováno {result.totalCount} pohybů ze souboru {result.fileName}.
            </p>
            <p>
              {result.createdCount} nových · {result.updatedCount} aktualizovaných · {result.unchangedCount} beze změny
            </p>
            <Link to={`/doklady?status=ALL&importId=${result.id}`} className="mt-1 inline-block text-green-700 underline">
              Zobrazit pohyby z tohoto importu →
            </Link>
          </div>
        )}
      </Card>

      <Card>
        <h2 className="mb-3 font-semibold">Historie importů</h2>
        {history.length === 0 ? (
          <p className="text-sm text-slate-400">Zatím nic.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[560px] text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-slate-400">
                  <th className="py-2">Kdy</th>
                  <th className="py-2">Soubor</th>
                  <th className="py-2 text-right">Celkem</th>
                  <th className="py-2 text-right">Nové</th>
                  <th className="py-2 text-right">Změněné</th>
                  <th className="py-2 pl-6">Kdo</th>
                </tr>
              </thead>
              <tbody>
                {history.map((b) => (
                  <tr key={b.id} className="border-t border-slate-100">
                    <td className="py-2 whitespace-nowrap">{formatDateTime(b.createdAt)}</td>
                    <td className="py-2">
                      <Link to={`/doklady?status=ALL&importId=${b.id}`} className="text-brand-600 hover:underline">
                        {b.fileName}
                      </Link>
                    </td>
                    <td className="py-2 text-right tabular-nums">{b.totalCount}</td>
                    <td className="py-2 text-right tabular-nums">{b.createdCount}</td>
                    <td className="py-2 text-right tabular-nums">{b.updatedCount}</td>
                    <td className="py-2 pl-6 text-slate-500">{b.importedByEmail}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}
